import {
  clearSession,
  clearStepUp,
  getAccessToken,
  getAmr,
  getSessionUser,
  getStepUpToken,
  setSession,
  setStepUp,
  type SessionUser,
} from './session'
import {
  browserSupportsWebAuthn,
  ceremonyAuthenticate,
  ceremonyRegister,
  WebAuthnUserError,
} from './webauthnCeremony'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'

export const API_BASE = '/mail-api'

export class ApiError extends Error {
  status: number
  detail: unknown
  constructor(status: number, detail: unknown, message?: string) {
    super(message || `api_${status}`)
    this.status = status
    this.detail = detail
  }
}

function formatApiDetail(detail: unknown): string {
  if (detail == null) return 'request_failed'
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object' && 'msg' in item) {
          const loc = Array.isArray((item as { loc?: unknown }).loc)
            ? (item as { loc: unknown[] }).loc.join('.')
            : ''
          return loc ? `${loc}: ${(item as { msg: string }).msg}` : String((item as { msg: string }).msg)
        }
        return JSON.stringify(item)
      })
      .join('; ')
  }
  if (typeof detail === 'object' && detail) {
    const d = detail as {
      detail?: unknown
      code?: string
      message?: string
      error?: string
      stage?: string
      warning?: string
      hint?: string
    }
    if (d.code === 'hosts_required') {
      return d.message || 'imap_host and smtp_host required'
    }
    if (d.code === 'verify_failed') {
      const err = String(d.error || d.message || '')
      if (/AUTHENTICATIONFAILED|imap_auth_failed/i.test(err)) {
        return 'imap_auth_failed'
      }
      if (/imap_timeout/i.test(err)) return 'imap_timeout'
      if (/SSLCert|Certificate|CERT_VERIFY/i.test(err)) return 'smtp_ssl'
      if (/Timeout|SMTPConnectTimeout/i.test(err)) return 'smtp_timeout'
      if (/smtp_failed|Auth/i.test(err)) return 'smtp_failed'
      const stage = d.stage ? `${d.stage}: ` : ''
      return `${stage}${err || 'verify_failed'}`
    }
    if (d.code === 'suspicious_host') {
      return [d.warning, d.hint].filter(Boolean).join(' — ') || 'suspicious_host'
    }
    if (typeof d.message === 'string' && d.message) return d.message
    if (typeof d.error === 'string' && d.error) return d.error
    if ('detail' in d) return formatApiDetail(d.detail)
  }
  try {
    return JSON.stringify(detail)
  } catch {
    return 'request_failed'
  }
}

async function parseError(r: Response): Promise<ApiError> {
  let detail: unknown = null
  try {
    detail = await r.json()
  } catch {
    detail = await r.text().catch(() => null)
  }
  const msg =
    typeof detail === 'object' && detail && 'detail' in detail
      ? formatApiDetail((detail as { detail: unknown }).detail)
      : formatApiDetail(detail) || `http_${r.status}`
  return new ApiError(r.status, detail, msg)
}

type ApiOpts = {
  method?: string
  body?: unknown
  auth?: boolean
  stepUp?: boolean
  signal?: AbortSignal
  /** Internal: skip one refresh retry (avoid loops). */
  _retried?: boolean
}

let refreshInFlight: Promise<boolean> | null = null

/** Cookie refresh → new access token. Single-flight. */
async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const r = await fetch(`${API_BASE}/auth/token/refresh`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: '{}',
      })
      if (!r.ok) return false
      const data = (await r.json()) as { access_token?: string; session_id?: string }
      if (!data.access_token) return false
      const prev = getSessionUser()
      setSession({
        accessToken: data.access_token,
        user: prev || { id: 'pending', email: '', is_admin: false, tenant_id: 'pending' },
        amr: getAmr(),
        sessionId: data.session_id ?? undefined,
      })
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

export async function apiFetch<T = unknown>(path: string, opts: ApiOpts = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (opts.auth !== false) {
    const tok = getAccessToken()
    if (tok) headers.Authorization = `Bearer ${tok}`
  }
  if (opts.stepUp) {
    const su = getStepUpToken()
    if (su) headers['X-Step-Up'] = su
  }
  const r = await fetch(`${API_BASE}${path}`, {
    method: opts.method || (opts.body !== undefined ? 'POST' : 'GET'),
    credentials: 'same-origin',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  })
  // D2: expired access JWT → cookie refresh once, then retry
  if (
    r.status === 401 &&
    opts.auth !== false &&
    !opts._retried &&
    !path.includes('/auth/token/refresh')
  ) {
    const ok = await refreshAccessToken()
    if (ok) return apiFetch<T>(path, { ...opts, _retried: true })
  }
  if (!r.ok) throw await parseError(r)
  if (r.status === 204) return undefined as T
  return (await r.json()) as T
}

/** Authenticated binary GET (attachments / external-image) with same 401→refresh as apiFetch. */
export async function apiFetchBlob(
  path: string,
  opts: { signal?: AbortSignal; accept?: string; _retried?: boolean } = {},
): Promise<Response> {
  const headers: Record<string, string> = { Accept: opts.accept || '*/*' }
  const tok = getAccessToken()
  if (tok) headers.Authorization = `Bearer ${tok}`
  const r = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers,
    signal: opts.signal,
  })
  if (r.status === 401 && !opts._retried && !path.includes('/auth/token/refresh')) {
    const ok = await refreshAccessToken()
    if (ok) return apiFetchBlob(path, { ...opts, _retried: true })
  }
  return r
}

export type LangEntry = {
  code: string
  name_en: string
  name_native: string
  rtl: boolean
  ui_pack: boolean
  /** stable = human-verified; beta = 7GCOM MT pack */
  ui_tier?: 'stable' | 'beta' | 'none'
  region?: string | null
  mt_supported?: string | null
}

export type LanguageCatalog = {
  source: string
  count: number
  languages: LangEntry[]
  ui_packs: string[]
  ui_stable?: string[]
  ui_beta?: string[]
  fallback_ui: string
  stale?: boolean
}

export type TokenBundle = {
  access_token: string
  refresh_token?: string
  token_type: string
  expires_in: number
  session_id: string
  user: SessionUser
  amr: string[]
}

export type MeResponse = {
  user_id: string
  tenant_id: string
  email: string | null
  display_name?: string | null
  has_avatar?: boolean
  passkey_count?: number
  is_admin: boolean
  session_id: string | null
  amr: string[]
}

export type AuthSessionRow = {
  id: string
  amr: string[]
  device_label: string | null
  user_agent: string | null
  ip_prefix: string | null
  trust_level: string | null
  created_at: string
  last_seen_at: string | null
  expires_at: string
  current: boolean
}

export type HealthPayload = {
  status: string
  version?: string
  oauth?: { providers_enabled?: string[] }
}

function applyBundle(bundle: TokenBundle) {
  setSession({
    accessToken: bundle.access_token,
    user: bundle.user,
    amr: bundle.amr,
    sessionId: bundle.session_id,
  })
  clearStepUp()
}

export async function fetchLanguageCatalog(): Promise<LanguageCatalog> {
  return apiFetch('/i18n/languages', { auth: false })
}

export async function fetchOauthStatus(): Promise<{ providers_enabled: string[] }> {
  return apiFetch('/oauth/status', { auth: false })
}

export async function fetchApiHealth(): Promise<HealthPayload> {
  return apiFetch('/health', { auth: false })
}

/** Bootstrap: cookie refresh → /me. */
export async function bootstrapSession(): Promise<boolean> {
  try {
    const refreshed = await apiFetch<{
      access_token: string
      session_id: string
    }>('/auth/token/refresh', { method: 'POST', body: {}, auth: false })
    setSession({
      accessToken: refreshed.access_token,
      user: { id: 'pending', email: '', is_admin: false, tenant_id: 'pending' },
      sessionId: refreshed.session_id,
    })
    const me = await apiFetch<MeResponse>('/auth/me')
    setSession({
      accessToken: refreshed.access_token,
      user: {
        id: me.user_id,
        email: me.email || '',
        is_admin: me.is_admin,
        tenant_id: me.tenant_id,
        display_name: me.display_name ?? null,
        has_avatar: Boolean(me.has_avatar),
      },
      amr: me.amr || [],
      sessionId: me.session_id,
    })
    return true
  } catch {
    clearSession()
    return false
  }
}

export async function logout(): Promise<void> {
  try {
    await apiFetch('/auth/logout', { method: 'POST' })
  } catch {
    /* ignore */
  }
  clearSession()
}

export function supportsPasskeys(): boolean {
  return browserSupportsWebAuthn()
}

export async function passkeyLogin(email?: string): Promise<TokenBundle> {
  const body = email?.trim() ? { email: email.trim() } : {}
  const options = await apiFetch<PublicKeyCredentialRequestOptionsJSON>(
    '/auth/webauthn/login/options',
    { method: 'POST', body, auth: false },
  )
  try {
    const credential = await ceremonyAuthenticate(options)
    const bundle = await apiFetch<TokenBundle>('/auth/webauthn/login/verify', {
      method: 'POST',
      body: { credential },
      auth: false,
    })
    applyBundle(bundle)
    return bundle
  } catch (err) {
    if (err instanceof WebAuthnUserError) throw err
    throw err
  }
}

export async function registerPasskey(friendlyName?: string): Promise<{ credential_db_id: string }> {
  const options = await apiFetch<PublicKeyCredentialCreationOptionsJSON>(
    '/auth/webauthn/register/options',
    { method: 'POST' },
  )
  const credential = await ceremonyRegister(options)
  return apiFetch('/auth/webauthn/register/verify', {
    method: 'POST',
    body: { credential, friendly_name: friendlyName || 'Primary' },
  })
}

export async function fetchSignupStatus(): Promise<{
  public_signup_enabled: boolean
  email_code_login_enabled?: boolean
}> {
  return apiFetch('/auth/signup/status', { auth: false })
}

export async function fetchEmailCodeStatus(): Promise<{ email_code_login_enabled: boolean }> {
  return apiFetch('/auth/email-code/status', { auth: false })
}

export async function requestEmailCode(email: string, lang?: string): Promise<{
  ok: boolean
  message?: string
  debug_code?: string
}> {
  return apiFetch('/auth/email-code/request', {
    method: 'POST',
    body: { email: email.trim(), lang },
    auth: false,
  })
}

export async function verifyEmailCode(email: string, code: string): Promise<TokenBundle & { is_new?: boolean }> {
  const bundle = await apiFetch<TokenBundle & { is_new?: boolean }>('/auth/email-code/verify', {
    method: 'POST',
    body: { email: email.trim(), code: code.trim() },
    auth: false,
  })
  applyBundle(bundle)
  return bundle
}

export async function passkeySignup(email?: string): Promise<TokenBundle> {
  const body = email?.trim() ? { email: email.trim() } : {}
  const options = await apiFetch<PublicKeyCredentialCreationOptionsJSON>(
    '/auth/webauthn/signup/options',
    { method: 'POST', body, auth: false },
  )
  const credential = await ceremonyRegister(options)
  const bundle = await apiFetch<TokenBundle>('/auth/webauthn/signup/verify', {
    method: 'POST',
    body: { credential, friendly_name: 'Primary' },
    auth: false,
  })
  applyBundle(bundle)
  return bundle
}

export async function stepUpPasskey(): Promise<{ step_up_token: string; expires_in: number }> {
  const options = await apiFetch<PublicKeyCredentialRequestOptionsJSON>(
    '/auth/webauthn/step-up/options',
    { method: 'POST' },
  )
  const credential = await ceremonyAuthenticate(options)
  const result = await apiFetch<{ step_up_token: string; expires_in: number }>(
    '/auth/webauthn/step-up/verify',
    { method: 'POST', body: { credential } },
  )
  setStepUp(result.step_up_token, result.expires_in)
  return result
}

export async function recoveryBegin(email: string): Promise<{ ok: boolean; ticket: string | null; message: string }> {
  return apiFetch('/auth/recovery/begin', {
    method: 'POST',
    body: { email },
    auth: false,
  })
}

export async function recoveryVerify(opts: {
  ticket: string
  recovery_code?: string
  totp?: string
}): Promise<TokenBundle> {
  const bundle = await apiFetch<TokenBundle>('/auth/recovery/verify', {
    method: 'POST',
    body: opts,
    auth: false,
  })
  applyBundle(bundle)
  return bundle
}

export async function listSessions(): Promise<{ sessions: AuthSessionRow[] }> {
  return apiFetch('/auth/sessions')
}

export async function revokeSession(sessionId: string): Promise<void> {
  await apiFetch(`/auth/sessions/${sessionId}`, { method: 'DELETE' })
}

export async function logoutAll(): Promise<void> {
  await apiFetch('/auth/logout-all', { method: 'POST', stepUp: true })
  clearSession()
}

export async function listPasskeys(): Promise<{
  credentials: Array<{
    id: string
    credential_id: string
    friendly_name: string | null
    created_at: string
    last_used_at: string | null
  }>
}> {
  return apiFetch('/auth/webauthn/credentials')
}

export async function revokePasskey(credId: string): Promise<void> {
  await apiFetch(`/auth/webauthn/credentials/${credId}`, { method: 'DELETE', stepUp: true })
}

export async function enrollRecoveryCodes(): Promise<{ codes: string[]; count: number }> {
  return apiFetch('/auth/recovery/enroll/codes', { method: 'POST' })
}

export async function rotateRecoveryCodes(): Promise<{ codes: string[]; count: number }> {
  return apiFetch('/auth/recovery/enroll/codes/rotate', { method: 'POST', stepUp: true })
}

export async function totpStart(): Promise<{ secret: string; otpauth_uri: string }> {
  return apiFetch('/auth/recovery/enroll/totp/start', { method: 'POST' })
}

export async function totpConfirm(code: string): Promise<{ ok: boolean }> {
  return apiFetch('/auth/recovery/enroll/totp/confirm', { method: 'POST', body: { code } })
}

export async function fetchMe(): Promise<MeResponse> {
  return apiFetch('/auth/me')
}

export async function patchProfile(opts: { display_name: string | null }): Promise<{
  ok: boolean
  display_name: string | null
}> {
  return apiFetch('/auth/profile', { method: 'PATCH', body: opts })
}

export async function uploadMyAvatar(file: File): Promise<{ ok: boolean; has_avatar: boolean }> {
  const fd = new FormData()
  fd.set('file', file)
  const headers: Record<string, string> = { Accept: 'application/json' }
  const tok = getAccessToken()
  if (tok) headers.Authorization = `Bearer ${tok}`
  const r = await fetch(`${API_BASE}/auth/me/avatar`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers,
    body: fd,
  })
  if (!r.ok) throw await parseError(r)
  return (await r.json()) as { ok: boolean; has_avatar: boolean }
}

export async function deleteMyAvatar(): Promise<void> {
  await apiFetch('/auth/me/avatar', { method: 'DELETE' })
}

/** Authenticated user avatar → blob URL (Bearer). */
export async function fetchMyAvatarBlob(): Promise<string | null> {
  const tok = getAccessToken()
  if (!tok) return null
  try {
    const r = await fetch(`${API_BASE}/auth/me/avatar`, {
      headers: { Authorization: `Bearer ${tok}`, Accept: 'image/*' },
      credentials: 'same-origin',
    })
    if (!r.ok) return null
    const blob = await r.blob()
    return URL.createObjectURL(blob)
  } catch {
    return null
  }
}

export async function oauthStart(
  provider: 'microsoft' | 'gmail',
  opts?: { purpose?: 'mail' | 'calendar' | 'contacts'; account_id?: string; write?: boolean },
): Promise<{
  authorize_url: string
  provider: string
  expires_in_sec: number
  purpose?: string
}> {
  return apiFetch('/oauth/start', {
    method: 'POST',
    body: {
      provider,
      purpose: opts?.purpose || 'mail',
      account_id: opts?.account_id,
      write: opts?.write || false,
    },
  })
}

export type CalendarAccountStatus = {
  account_id: string
  label: string
  username: string
  provider: string
  calendar_connected: boolean
  calendar_write: boolean
}

export type CalendarEvent = {
  id: string
  account_id: string | null
  calendar_id: string | null
  provider_event_id: string | null
  title: string
  description: string
  location: string
  starts_at: string
  ends_at: string
  all_day: boolean
  status: string
  conflict_state: string
  local_dirty: boolean
  provider: string | null
  account_label: string
  local_only?: boolean
}

export async function fetchCalendarStatus(): Promise<{
  enabled: boolean
  accounts: CalendarAccountStatus[]
}> {
  return apiFetch('/calendars/status')
}

export async function syncCalendar(accountId: string): Promise<{
  ok: boolean
  error?: string
  calendars?: number
  events?: number
}> {
  return apiFetch(`/calendars/sync?account_id=${encodeURIComponent(accountId)}`, {
    method: 'POST',
  })
}

export async function listCalendarEvents(opts?: {
  start?: string
  end?: string
  account_id?: string
}): Promise<{ count: number; items: CalendarEvent[] }> {
  const q = new URLSearchParams()
  if (opts?.start) q.set('start', opts.start)
  if (opts?.end) q.set('end', opts.end)
  if (opts?.account_id) q.set('account_id', opts.account_id)
  const qs = q.toString()
  return apiFetch(`/calendars/events${qs ? `?${qs}` : ''}`)
}

export async function createCalendarEvent(body: {
  account_id?: string | null
  title: string
  starts_at: string
  ends_at: string
  all_day?: boolean
  description?: string
  location?: string
}): Promise<{
  id: string
  local_dirty?: boolean
  local_only?: boolean
  needs_write_consent?: boolean
  account_id?: string | null
}> {
  return apiFetch('/calendars/events', { method: 'POST', body })
}

export async function pushCalendarEvent(id: string): Promise<{
  ok: boolean
  needs_write_consent?: boolean
  account_id?: string | null
  local_only?: boolean
}> {
  return apiFetch(`/calendars/events/${id}/push`, { method: 'POST' })
}

export async function patchCalendarEvent(
  id: string,
  body: Record<string, unknown>,
): Promise<{
  id: string
  ok: boolean
  needs_write_consent?: boolean
  account_id?: string | null
}> {
  return apiFetch(`/calendars/events/${id}`, { method: 'PATCH', body })
}

export async function deleteCalendarEvent(id: string): Promise<{
  deleted: string
  needs_write_consent?: boolean
  account_id?: string | null
}> {
  return apiFetch(`/calendars/events/${id}`, { method: 'DELETE' })
}

export type ContactsAccountStatus = {
  account_id: string
  label: string
  username: string
  provider: string
  contacts_connected: boolean
  contacts_write: boolean
}

export type ContactItem = {
  id: string
  email: string
  display_name: string | null
  sources: string[]
  source: string
  message_count: number
  last_contact_at: string | null
  has_sync: boolean
  has_manual: boolean
  has_mail: boolean
  account_id?: string | null
  account_ids?: string[]
}

export async function fetchContactsStatus(): Promise<{
  enabled: boolean
  accounts: ContactsAccountStatus[]
}> {
  return apiFetch('/contacts/status')
}

export async function syncContacts(accountId?: string): Promise<{
  ok: boolean
  error?: string | null
  contacts?: number
  mail_derived?: number
  needs_reconnect?: boolean
}> {
  const qs = accountId ? `?account_id=${encodeURIComponent(accountId)}` : ''
  return apiFetch(`/contacts/sync${qs}`, { method: 'POST' })
}

export async function listContacts(opts?: {
  q?: string
  limit?: number
  offset?: number
}): Promise<{ count: number; total: number; items: ContactItem[] }> {
  const q = new URLSearchParams()
  if (opts?.q) q.set('q', opts.q)
  if (opts?.limit != null) q.set('limit', String(opts.limit))
  if (opts?.offset != null) q.set('offset', String(opts.offset))
  const qs = q.toString()
  return apiFetch(`/contacts${qs ? `?${qs}` : ''}`)
}

export async function createContact(body: {
  email: string
  display_name?: string
}): Promise<{
  ok: boolean
  email: string
  display_name?: string | null
  source: string
  sources?: string[]
}> {
  // Explicit /manual path — avoids any ambiguity with /contacts/sync
  return apiFetch('/contacts/manual', { method: 'POST', body })
}

export async function listContactHistory(
  email: string,
  limit = 50,
): Promise<{
  email: string
  count: number
  items: Array<{
    id: string
    account_id: string | null
    from_addr: string | null
    from_name: string | null
    subject: string | null
    date_sent: string | null
    folder: string | null
    direction: string
  }>
}> {
  const q = new URLSearchParams({ email, limit: String(limit) })
  return apiFetch(`/contacts/history?${q}`)
}

export type ImageDisplayMode = 'ask' | 'always' | 'never'

export type MailAccount = {
  id: string
  label: string
  provider: string
  username: string
  sync_enabled: boolean
  unread_count?: number
  total_inbox?: number
  total_all?: number
  has_avatar?: boolean
  last_sync_at?: string | null
  last_sync_status?: string | null
  last_sync_error?: string | null
  last_uid_seen?: number | null
  /** FAZA 1.5 — account roof for remote images (default ask). */
  image_display_mode?: ImageDisplayMode
}

export type MessageSummary = {
  id: string
  account_id: string
  from_addr: string | null
  from_name: string | null
  subject: string | null
  date_sent: string | null
  body_preview: string
  body_translated_preview: string | null
  flags: { read?: boolean; starred?: boolean; archived?: boolean }
  folder: string
  has_attachments: boolean
  lang_original?: string | null
  translation_engine?: string | null
  importance?: string | null
  thread_id?: string | null
  /** Owner sticky note (Faza notes) — list shows icon when true. */
  has_note?: boolean
}

export type MessageDetail = MessageSummary & {
  body_text: string | null
  body_html: string | null
  body_translated: string | null
  to_addrs: string[]
  cc_addrs: string[]
  lang_target?: string | null
  user_note?: string | null
  attachment_names?: Array<{
    filename?: string
    content_type?: string
    content_id?: string | null
    saved?: boolean
    size?: number
  }>
}

export async function listAccounts(): Promise<{ count: number; items: MailAccount[] }> {
  return apiFetch('/accounts/')
}

export async function deleteAccount(accountId: string): Promise<{
  deleted: string
  cleared_messages: number
  cleared_attachment_dirs?: number
  secret_removed?: unknown
}> {
  return apiFetch(`/accounts/${accountId}`, { method: 'DELETE' })
}

export async function patchAccount(
  accountId: string,
  body: {
    label?: string
    sync_enabled?: boolean
    image_display_mode?: ImageDisplayMode
  },
): Promise<{
  id: string
  updated: boolean
  changed_fields?: string[]
  message?: string
}> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}`, {
    method: 'PATCH',
    body,
  })
}

/** Trigger provider sync for one account (Gmail/Graph/IMAP). */
export async function syncAccount(accountId: string): Promise<{
  ok?: boolean
  fetched?: number
  inserted?: number
  error?: string
}> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/sync`, { method: 'POST' })
}

/** Trigger sync for all sync-enabled accounts (all-mail view). */
export async function syncAllAccounts(): Promise<{
  accounts_total?: number
  inserted_total?: number
  results?: Array<{ account_id?: string; inserted?: number; fetched?: number }>
}> {
  return apiFetch('/accounts/_sync-all', { method: 'POST' })
}

export async function listMessages(opts?: {
  account_id?: string
  folder?: string
  unread_only?: boolean
  starred_only?: boolean
  noted_only?: boolean
  priority_only?: boolean
  importance?: 'low' | 'normal' | 'high' | 'urgent'
  limit?: number
  offset?: number
}): Promise<{ count: number; total: number; limit: number; offset: number; items: MessageSummary[] }> {
  const q = new URLSearchParams()
  if (opts?.account_id) q.set('account_id', opts.account_id)
  if (opts?.folder) q.set('folder', opts.folder)
  if (opts?.unread_only) q.set('unread_only', 'true')
  if (opts?.starred_only) q.set('starred_only', 'true')
  if (opts?.noted_only) q.set('noted_only', 'true')
  if (opts?.priority_only) q.set('priority_only', 'true')
  if (opts?.importance) q.set('importance', opts.importance)
  if (opts?.limit != null) q.set('limit', String(opts.limit))
  if (opts?.offset != null) q.set('offset', String(opts.offset))
  const qs = q.toString()
  return apiFetch(`/messages/${qs ? `?${qs}` : ''}`)
}

export async function searchMessages(opts: {
  q: string
  account_id?: string
  limit?: number
  offset?: number
}): Promise<{
  count: number
  total?: number
  limit?: number
  offset?: number
  items: MessageSummary[]
  parsed?: Record<string, unknown>
}> {
  const q = new URLSearchParams()
  q.set('q', opts.q)
  if (opts.account_id) q.set('account_id', opts.account_id)
  if (opts.limit != null) q.set('limit', String(opts.limit))
  if (opts.offset != null) q.set('offset', String(opts.offset))
  return apiFetch(`/messages/search/q?${q.toString()}`)
}

export async function fetchProviderCatalog(): Promise<{
  items: Array<{
    id: string
    label: string
    oauth: boolean
    domains: string[]
    imap_host: string | null
    imap_port: number
    smtp_host: string | null
    smtp_port: number
    capabilities: Record<string, unknown>
    app_password_hint: string | null
  }>
  remote_autoconfig: boolean
}> {
  return apiFetch('/accounts/catalog')
}

export async function suggestAutoconfig(email: string): Promise<{
  ok: boolean
  source?: string
  provider?: string
  email?: string
  imap_host?: string | null
  imap_port?: number
  smtp_host?: string | null
  smtp_port?: number
  oauth_preferred?: boolean
  app_password_hint?: string | null
  capabilities?: Record<string, unknown>
  message?: string
  error?: string
}> {
  const q = new URLSearchParams({ email })
  return apiFetch(`/accounts/autoconfig?${q.toString()}`)
}

export async function connectAccount(body: {
  email: string
  password: string
  label?: string
  provider?: string
  auth_username?: string
  imap_host?: string
  imap_port?: number
  smtp_host?: string
  smtp_port?: number
  imap_starttls?: boolean
  acknowledge_warning?: boolean
}): Promise<{ ok: boolean; id: string; provider: string; credential_provisioned: boolean }> {
  return apiFetch('/accounts/connect', { method: 'POST', body })
}


export async function getMessage(id: string): Promise<MessageDetail> {
  return apiFetch(`/messages/${id}`)
}

/** D1: mark read/starred/archived (server JSONB merge). */
export async function setMessageFlags(
  id: string,
  flags: { read?: boolean; starred?: boolean; archived?: boolean },
): Promise<{ id: string; flags: MessageSummary['flags'] }> {
  return apiFetch(`/messages/${id}/flag`, { method: 'POST', body: flags })
}

/** Toggle/clear owner sticky note (notice). Empty/null clears. */
export async function setMessageNote(
  id: string,
  note: string | null,
): Promise<{ id: string; user_note: string | null; has_note: boolean }> {
  return apiFetch(`/messages/${id}/note`, { method: 'POST', body: { note } })
}

export type UserSettings = {
  locale: string
  theme: 'light' | 'dark' | 'system'
  background: 'lavender' | 'warm' | 'sky' | 'mint' | 'neutral'
  font_size_px: number
  list_density: 'compact' | 'standard' | 'comfortable'
  ai_privacy: 'disabled' | 'local' | 'private_7gc' | 'external'
  a11y_high_contrast: boolean
  a11y_reduce_motion: boolean
  a11y_comfort: boolean
  a11y_voice_confirm: boolean
  account_prefs?: Record<string, { color?: string | null; initial?: string | null; label?: string | null }>
}

export async function getUserSettings(): Promise<{ settings: UserSettings }> {
  return apiFetch('/settings/')
}

export async function putUserSettings(
  patch: Partial<UserSettings>,
): Promise<{ settings: UserSettings; saved: boolean }> {
  return apiFetch('/settings/', { method: 'PUT', body: patch })
}

export type ReplyContext = {
  message_id: string
  from_account_id: string
  from_account_email: string
  from_account_label: string
  to: string[]
  to_all: string[]
  cc: string[]
  subject: string
  in_reply_to: string | null
  references: string | null
  quoted_text: string
  quoted_html: string
  source_body_text?: string
}

export type SendResult = {
  id: string
  status: 'queued' | 'sent' | 'failed' | 'uncertain' | 'sending' | string
  sent_message_id?: string | null
  to: string[]
  cc: string[]
  bcc_count: number
  attachments: { filename: string; size: number; content_type: string }[]
  undo_until?: string | null
  undo_seconds?: number
  message?: string
  duplicate?: boolean
  from_username?: string
  subject?: string
  sent_folder?: unknown
}

export async function fetchReplyContext(messageId: string): Promise<ReplyContext> {
  return apiFetch(`/messages/${messageId}/reply-context`)
}

export async function sendMessage(fields: {
  from_account_id: string
  to: string
  cc?: string
  bcc?: string
  subject?: string
  body_text?: string
  body_html?: string
  in_reply_to_message_id?: string | null
  in_reply_to_header?: string | null
  references_header?: string | null
  files?: File[]
  delay_seconds?: number
  idempotency_key?: string
}): Promise<SendResult> {
  const fd = new FormData()
  fd.set('from_account_id', fields.from_account_id)
  fd.set('to', fields.to)
  if (fields.cc) fd.set('cc', fields.cc)
  if (fields.bcc) fd.set('bcc', fields.bcc)
  if (fields.subject != null) fd.set('subject', fields.subject)
  if (fields.body_text != null) fd.set('body_text', fields.body_text)
  if (fields.body_html) fd.set('body_html', fields.body_html)
  if (fields.in_reply_to_message_id) fd.set('in_reply_to_message_id', fields.in_reply_to_message_id)
  if (fields.in_reply_to_header) fd.set('in_reply_to_header', fields.in_reply_to_header)
  if (fields.references_header) fd.set('references_header', fields.references_header)
  fd.set('delay_seconds', String(fields.delay_seconds ?? 15))
  if (fields.idempotency_key) fd.set('idempotency_key', fields.idempotency_key)
  for (const f of fields.files || []) fd.append('attachments', f)

  const headers: Record<string, string> = { Accept: 'application/json' }
  const tok = getAccessToken()
  if (tok) headers.Authorization = `Bearer ${tok}`

  const r = await fetch(`${API_BASE}/messages/send`, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: fd,
  })
  if (!r.ok) throw await parseError(r)
  return (await r.json()) as SendResult
}

export async function undoSend(pendingId: string): Promise<{ ok: boolean; id?: string; status?: string }> {
  return apiFetch(`/messages/send/${pendingId}/undo`, { method: 'POST' })
}

export async function getSendStatus(pendingId: string): Promise<{
  id: string
  status: string
  undo_until?: string | null
  sent_message_id?: string | null
  error?: string | null
}> {
  return apiFetch(`/messages/send/${pendingId}`)
}

/** Authenticated avatar bytes → blob (Bearer). Used so <img> is not stuck on 401. */
export async function fetchAccountAvatarBlob(accountId: string): Promise<Blob | null> {
  const headers: Record<string, string> = { Accept: 'image/*' }
  const tok = getAccessToken()
  if (tok) headers.Authorization = `Bearer ${tok}`
  try {
    const r = await fetch(`${API_BASE}/accounts/${accountId}/avatar`, {
      credentials: 'same-origin',
      headers,
    })
    if (!r.ok) return null
    const blob = await r.blob()
    if (!blob.type.startsWith('image/')) return null
    return blob
  } catch {
    return null
  }
}

export async function uploadAccountAvatar(
  accountId: string,
  file: File,
): Promise<{ ok: boolean; has_avatar: boolean }> {
  const fd = new FormData()
  fd.set('file', file)
  const headers: Record<string, string> = {}
  const tok = getAccessToken()
  if (tok) headers.Authorization = `Bearer ${tok}`
  const r = await fetch(`${API_BASE}/accounts/${accountId}/avatar`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers,
    body: fd,
  })
  if (!r.ok) {
    let detail = `HTTP ${r.status}`
    try {
      const j = (await r.json()) as { detail?: string }
      if (j.detail) detail = String(j.detail)
    } catch {
      /* ignore */
    }
    throw new ApiError(r.status, detail)
  }
  return (await r.json()) as { ok: boolean; has_avatar: boolean }
}

export async function deleteAccountAvatar(accountId: string): Promise<void> {
  await apiFetch(`/accounts/${accountId}/avatar`, { method: 'DELETE' })
}

export async function agentAsk(body: {
  question: string
  message_id?: string
  /** connect_help = provider/app-password advice; no mailbox access */
  scope?: 'connect_help' | string
}): Promise<{
  ok: boolean
  answer: string
  sources: { type: string; id?: string; subject?: string }[]
  privacy: string
  privacy_location?: string
  why?: string
  suggestion?: boolean
  injection_blocked?: boolean
}> {
  return apiFetch('/agent/ask', { method: 'POST', body })
}

export async function agentPrepare(body: {
  intent: string
  message_id?: string
  lang_hint?: string
}): Promise<{
  ok: boolean
  draft: string
  subject: string
  sources: { type: string; id?: string }[]
  privacy: string
  why: string
}> {
  return apiFetch('/agent/prepare', { method: 'POST', body })
}

export type TrustBadge = {
  id: string
  label: string
  detail: string
  state: 'pass' | 'fail' | 'warn' | 'unknown' | 'info' | string
}

export async function fetchMessageTrust(messageId: string): Promise<{
  from_addr: string
  from_name: string | null
  reply_to_addr?: string | null
  note?: string
  badges: TrustBadge[]
}> {
  return apiFetch(`/messages/${messageId}/trust`)
}

export async function checkLanguageQuality(messageId: string): Promise<{
  ok: boolean
  checked: boolean
  issues: string[]
  summary: string
}> {
  return apiFetch(`/messages/${messageId}/language-quality`, { method: 'POST', body: {} })
}

export async function checkLanguageQualityDraft(body: {
  original: string
  candidate: string
}): Promise<{ ok: boolean; checked: boolean; issues: string[]; summary: string }> {
  return apiFetch('/messages/language-quality/check', { method: 'POST', body })
}

export async function fetchMessageThread(messageId: string): Promise<{
  thread_id: string | null
  count: number
  items: MessageSummary[]
}> {
  return apiFetch(`/messages/${messageId}/thread`)
}

export type MailDraft = {
  id: string
  account_id: string | null
  to_addrs: string
  cc_addrs: string
  bcc_addrs: string
  subject: string
  body_text: string
  body_html: string
  in_reply_to_message_id: string | null
  in_reply_to_header: string | null
  references_header: string | null
  created_at: string | null
  updated_at: string | null
}

export type DraftPayload = {
  account_id?: string | null
  to_addrs?: string
  cc_addrs?: string
  bcc_addrs?: string
  subject?: string
  body_text?: string
  body_html?: string
  in_reply_to_message_id?: string | null
  in_reply_to_header?: string | null
  references_header?: string | null
}

export async function listDrafts(): Promise<{ count: number; items: MailDraft[] }> {
  return apiFetch('/drafts/')
}

export async function getDraft(id: string): Promise<MailDraft> {
  return apiFetch(`/drafts/${id}`)
}

export async function createDraft(body: DraftPayload): Promise<MailDraft> {
  return apiFetch('/drafts/', { method: 'POST', body })
}

export async function updateDraft(id: string, body: DraftPayload): Promise<MailDraft> {
  return apiFetch(`/drafts/${id}`, { method: 'PUT', body })
}

export async function deleteDraft(id: string): Promise<{ ok: boolean; id: string }> {
  return apiFetch(`/drafts/${id}`, { method: 'DELETE' })
}

